import { useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Spread } from '../shared/components/Spread.js';
import { Timer } from '../shared/components/Timer.js';
import { SecretWordLabel } from '../shared/components/SecretWordLabel.js';
import { RevealCast } from './components/RevealCast.js';
import type { GhostGuessingView } from './types.js';
import './reveal-arc.css';
import './GhostGuessing.css';

interface GhostGuessingProps {
  view: GhostGuessingView;
  /** The eliminated Ghost committing their final guess. In production this
   *  dispatches `submitGuess`; in the mock it's a design-review no-op the host
   *  can wire to advance the stage. Only ever called on the Ghost's own view. */
  onSubmitGuess?: (word: string) => void;
}

/**
 * GhostGuessing — the eliminated Ghost's one final guess at the word, printed
 * onto the cooled night spread. Two views off the same surface:
 *   - The Ghost's own (`isMine`): Timer at the recto top (sub-5s → Oxblood, the
 *     one place consequence enters this otherwise-Spectral phase), the eyebrow,
 *     "One final guess.", the length sentence, and a mono guess input.
 *   - The room's: just "{Name}, the Ghost, is guessing…" + the timer. No
 *     length, no eyebrow — the table only knows the Ghost is deciding.
 * The clues stay on the verso throughout: the Ghost deduces from the evidence.
 */
export function GhostGuessing({ view, onSubmitGuess }: GhostGuessingProps): JSX.Element {
  const { t } = useTranslation();
  const [guess, setGuess] = useState('');
  const struckIds = useMemo(() => new Set<string>([view.guessingGhost.id]), [view.guessingGhost.id]);

  const trimmed = guess.trim();
  const submit = (): void => {
    if (trimmed.length > 0) onSubmitGuess?.(trimmed);
  };

  /* Palette persists per viewer: the Ghost sees their cool-night world; the
     village watches in warm candlelight (same treatment as RoleReveal). */
  const cooled = view.pov === 'ghost';

  return (
    <div className="susdle-reveal" data-mood={cooled ? 'night' : 'candle'}>
      <h1 className="susdle-sr-only">{t('reveal.ghostGuessing.ariaLabel')}</h1>
      <Spread
        pov={view.pov}
        ariaLabel={t('reveal.ghostGuessing.ariaLabel')}
        overlay={<div className="susdle-spread-candle" data-mood={cooled ? 'night' : 'reveal'} aria-hidden />}
        verso={
          <div className="susdle-reveal-verso">
            <RevealCast players={view.cast} struckIds={struckIds} />
          </div>
        }
        recto={
          <div className="susdle-guess-recto">
            <div className="susdle-guess-recto__timer">
              <Timer phaseEndsAt={view.phaseEndsAt} durationMs={view.durationMs} />
            </div>
            {view.isMine ? (
              <div className="susdle-guess">
                <div className="susdle-guess__head">
                  <p className="susdle-guess__prompt">{t('reveal.ghostGuessing.prompt')}</p>
                  <SecretWordLabel mode="length" length={view.secretLength} />
                </div>
                <div className="susdle-guess__action">
                  <form
                    className="susdle-guess__input"
                    onSubmit={(e) => {
                      e.preventDefault();
                      submit();
                    }}
                  >
                    <input
                      className="susdle-guess__field"
                      name="guess"
                      value={guess}
                      onChange={(e) => setGuess(e.target.value)}
                      spellCheck={false}
                      autoComplete="off"
                      autoCapitalize="characters"
                      maxLength={view.secretLength > 0 ? view.secretLength : undefined}
                      aria-label={t('reveal.ghostGuessing.inputAria')}
                      data-testid="ghost-guess-input"
                    />
                  </form>
                  <button
                    type="button"
                    className="susdle-action"
                    data-tone="ghost"
                    onClick={submit}
                    disabled={trimmed.length === 0}
                    data-testid="ghost-guess-submit"
                  >
                    {t('reveal.ghostGuessing.submit')}
                  </button>
                </div>
              </div>
            ) : (
              <p className="susdle-guess__watching" aria-live="polite">
                {t('reveal.ghostGuessing.watching', { name: view.guessingGhost.name })}
              </p>
            )}
          </div>
        }
      />
    </div>
  );
}
